import { useMemo } from 'react';
import { useAppSelector } from './useAppSelector';

/**
 * Custom hook to get filtered companies based on Redux filter state
 * This centralizes the filtering logic for consistent navigation
 */
export const useFilteredCompanies = () => {
  // Get all companies from Redux
  const allCompanies = useAppSelector(state => state.supaPages.pages);
  const supaCompanies = useAppSelector(state => state.supaCompanies.companies);
  
  // Get filter state from Redux
  const selectedCategories = useAppSelector(state => state.companiesFilter.selectedCategories);
  const selectedEmployees = useAppSelector(state => state.companiesFilter.selectedEmployees);
  const selectedProductCount = useAppSelector(state => state.companiesFilter.selectedProductCount);
  const selectedModalities = useAppSelector(state => state.companiesFilter.selectedModalities);
  const selectedTags = useAppSelector(state => state.companiesFilter.selectedTags);
  const slugFilter = useAppSelector(state => state.companiesFilter.slugFilter);
  
  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      (selectedCategories.length > 0 && !selectedCategories.includes('All')) ||
      (selectedEmployees.length > 0 && !selectedEmployees.includes('All')) ||
      (selectedProductCount.length > 0 && !selectedProductCount.includes('All')) ||
      (selectedModalities.length > 0 && !selectedModalities.includes('All')) ||
      (selectedTags.length > 0 && !selectedTags.includes('All')) ||
      (slugFilter && slugFilter.trim() !== '')
    );
  }, [selectedCategories, selectedEmployees, selectedProductCount, selectedModalities, selectedTags, slugFilter]);
  
  // Apply filters to get filtered companies
  const filteredCompanies = useMemo(() => {
    // Determine which companies array to use
    const companiesArray = allCompanies && allCompanies.length > 0 
      ? allCompanies 
      : supaCompanies || [];
    
    if (!hasActiveFilters || companiesArray.length === 0) {
      return companiesArray;
    }
    
    console.log('🔍 [useFilteredCompanies] Applying filters to companies:', {
      total: companiesArray.length,
      filters: {
        categories: selectedCategories,
        employees: selectedEmployees,
        productCount: selectedProductCount,
        modalities: selectedModalities,
        tags: selectedTags,
        slug: slugFilter
      }
    });
    
    return companiesArray.filter(company => {
      // Slug filter
      const slugMatch = !slugFilter || 
        company.slug?.toLowerCase().includes(slugFilter.toLowerCase()) || 
        company.name?.toLowerCase().includes(slugFilter.toLowerCase()) ||
        company.description?.toLowerCase().includes(slugFilter.toLowerCase());
      
      // Category filter
      const categoryMatch = 
        !selectedCategories?.length || 
        selectedCategories.includes('All') || 
        selectedCategories.includes(company.category || '');
      
      // Employee filter
      const employeeMatch = 
        !selectedEmployees?.length || 
        selectedEmployees.includes('All') || 
        selectedEmployees.includes(company.employees || '');
      
      // Product count filter
      const hasMultiplePages = Array.isArray(company.pages) && company.pages.length > 1;
      const productCountMatch = 
        !selectedProductCount?.length || 
        selectedProductCount.includes('All') ||
        (selectedProductCount.includes('Multiple Products') && hasMultiplePages) ||
        (selectedProductCount.includes('Single Product') && !hasMultiplePages);
      
      // Modality filter
      const allowedModalities = ['Credit', 'Usage', 'License'];
      let modalityMatch = true;
      
      if (selectedModalities?.length) {
        if (selectedModalities.includes('All')) {
          modalityMatch = true;
        } else if (selectedModalities.includes('None')) {
          modalityMatch = !company.pricingMetrics || 
                company.pricingMetrics.length === 0 || 
                company.pricingMetrics.every(metric => !metric.modality || !allowedModalities.includes(metric.modality));
        } else {
          const companyModalities = new Set(
            company.pricingMetrics
              ?.filter(metric => metric.modality && allowedModalities.includes(metric.modality))
              .map(metric => metric.modality) || []
          );
          
          modalityMatch = selectedModalities.every(selectedModality => 
            companyModalities.has(selectedModality || '')
          );
        }
      }
      
      // Tag filter
      let tagMatch = true;
      
      if (selectedTags?.length && !selectedTags.includes('All')) {
        // Helper function to normalize a tag
        const normalizeTag = (tag: any): string | null => {
          if (!tag) return null;
          
          // If tag is a string, return it directly
          if (typeof tag === 'string') {
            return tag;
          }
          
          // If tag has a name property, return that
          if (tag.name) {
            return tag.name;
          }
          
          // If tag has a nested tag property, extract the name
          if (tag.tag && tag.tag.name) {
            return tag.tag.name;
          }
          
          return null;
        };
        
        // Handle case where company.tags might not be an array or might be undefined
        const companyTags = company.tags || [];
        
        if (Array.isArray(companyTags) && companyTags.length > 0) {
          // Extract normalized tag names from company tags
          const normalizedTags = companyTags
            .map(normalizeTag)
            .filter(Boolean) as string[];
          
          // Check if any of the company's normalized tags match the selected tags
          tagMatch = normalizedTags.some(tagName => 
            selectedTags.includes(tagName)
          );
        } else {
          // If company has no tags, it doesn't match tag filter
          tagMatch = false;
        }
      }
      
      // Return true only if all filters match
      return slugMatch && categoryMatch && employeeMatch && productCountMatch && modalityMatch && tagMatch;
    });
  }, [
    allCompanies,
    supaCompanies,
    hasActiveFilters,
    selectedCategories,
    selectedEmployees,
    selectedProductCount,
    selectedModalities,
    selectedTags,
    slugFilter
  ]);
  
  // Get adjacent company slugs based on the filtered list
  const getAdjacentCompanySlugs = (currentSlug: string) => {
    const companies = filteredCompanies;
    
    if (companies.length === 0) {
      console.log('🔍 [useFilteredCompanies] No companies available for navigation');
      return { prevSlug: null, nextSlug: null };
    }
    
    const currentIndex = companies.findIndex(company => company.slug === currentSlug);
    
    if (currentIndex === -1) {
      console.log(`🔍 [useFilteredCompanies] Current company ${currentSlug} not found in filtered list`);
      
      // If the current company is not in the filtered list, find the next company alphabetically
      const alphabeticalIndex = companies.findIndex(company => company.slug > currentSlug);
      
      if (alphabeticalIndex !== -1) {
        const nextIndex = alphabeticalIndex;
        const prevIndex = nextIndex > 0 ? nextIndex - 1 : companies.length - 1;
        
        return {
          prevSlug: companies[prevIndex].slug,
          nextSlug: companies[nextIndex].slug
        };
      }
      
      // If we can't find a company that comes after this one, use the first and last
      if (companies.length > 0) {
        return {
          prevSlug: companies[companies.length - 1].slug,
          nextSlug: companies[0].slug
        };
      }
      
      return { prevSlug: null, nextSlug: null };
    }
    
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : companies.length - 1;
    const nextIndex = currentIndex < companies.length - 1 ? currentIndex + 1 : 0;
    
    console.log(`🔍 [useFilteredCompanies] Found adjacent companies for ${currentSlug}:`, {
      prev: companies[prevIndex].slug,
      next: companies[nextIndex].slug,
      totalFiltered: companies.length
    });
    
    return {
      prevSlug: companies[prevIndex].slug,
      nextSlug: companies[nextIndex].slug
    };
  };
  
  return {
    filteredCompanies,
    hasActiveFilters,
    getAdjacentCompanySlugs
  };
};
